import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { format, isSameWeek, isToday, isValid, isYesterday } from "date-fns";
import GroupedSectionHeader from "../../components/ui/GroupedSectionHeader";
import HoverActionsMenu from "../../components/HoverActionsMenu";
import ActivityAction from "../../actions/ActivityAction";
import { fromJS } from "immutable";
import { renderItemIcon } from "../component/picker-shared";
import { ArchiveRestore, Eye, Mail } from "lucide-react";
import ListViewEmptyState from "./ListViewEmptyState";
import TriangleAlertIcon from "../../icons/TriangleAlertIcon";
import OctagonAlertIcon from "../../icons/OctagonAlertIcon";
import LayoutAction from "../../actions/LayoutAction";
import { deleteDraftEmail } from "../../api/EmailApi";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import TaskAction from "../../actions/TaskAction";
import { DialogTypes } from "../../constants/action-types";
import { isGmailContext } from "../../api/api";

type EmailData = {
  id: string;
  itemId: string;
  title: string;
  recipients?: string[];
  cc?: string[];
  bcc?: string[];
  content?: string;
  preview?: string;
  provider?: string;
  messageId?: string;
  threadId?: string;
  emailStatus?: string;
  sendAtUtc?: number;
  senderName?: string;
  senderAvatar?: string;
  createdAt?: number;
  item?: any;
  tracking?: boolean;
  visibility?: string;
};

const formatWithTime = (date: Date) => {
  if (!isValid(date)) return `-`;
  if (isToday(date)) return `Today`;
  if (isYesterday(date)) return `Yesterday`;
  return format(date, "dd MMM yyyy");
};

const groupByTime = (emails: EmailData[]) => {
  const sorted = emails.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const today: EmailData[] = [];
  const thisWeek: EmailData[] = [];
  const months: Record<string, EmailData[]> = {};
  const now = new Date();
  const weekOptions = { weekStartsOn: 1 as const };

  sorted.forEach((t) => {
    const dt = t.createdAt ? new Date(t.createdAt * 1000) : undefined;
    if (!dt) {
      thisWeek.push(t);
      return;
    }
    if (isToday(dt)) {
      today.push(t);
      return;
    }
    if (isSameWeek(dt, now, weekOptions)) {
      thisWeek.push(t);
      return;
    }
    const label = format(dt, "MMMM yyyy");
    (months[label] ||= []).push(t);
  });

  return { today, thisWeek, months };
};

const GroupBlock = ({
  label,
  count,
  isCollapsed,
  onToggle,
  children,
}: {
  label: string;
  count: number;
  isCollapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => {
  return (
    <div className="w-full">
      <GroupedSectionHeader
        label={label}
        count={count}
        isCollapsed={isCollapsed}
        onToggle={onToggle}
      />
      {!isCollapsed && <div className="mt-1">{children}</div>}
    </div>
  );
};

const EmailItem = ({
  email,
  onOpen,
  onDelete,
}: {
  email: EmailData;
  onOpen: (email: EmailData) => void;
  onDelete: (email: EmailData) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId }: any = useParams();

  const recipientsText = email.recipients.map((r) => `@${r}`).join(" ");

  return (
    <div
      className="relative group flex items-center justify-start gap-2 px-2 py-2 hover:bg-gray-50 transition-colors rounded-md"
      onClick={() => onOpen(email)}
    >
      {/* Title */}
      <div className="flex-2 truncate cursor-default text-gray-900" title={email.title}>
        <div className="text-sm font-medium truncate">{email.title}</div>
        {email.preview ? (
          <div className="text-xs font-normal truncate">{email.preview}</div>
        ) : (
          <div className="text-xs font-normal truncate">&nbsp;</div>
        )}
      </div>

      {/* Sent to */}
      <div className="flex-2 flex items-center gap-1 truncate cursor-default">
        <img
          src={email.senderAvatar}
          alt={email.senderName}
          className="w-5 h-5 rounded-full flex-shrink-0"
        />
        <p className="text-sm font-medium text-gray-900">{email.senderName}</p>
        <p className="text-xs font-normal text-gray-500">{t("sent to")}</p>
        <span title={recipientsText} className="text-xs font-medium text-blue-600">
          {recipientsText}
        </span>
      </div>

      {/* Item */}
      <div
        className="flex-2 flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/workspaces/${projectId}/items/${email.itemId}?type=${email.item?.type}`);
        }}
      >
        {renderItemIcon({}, email.item?.type)}
        {email.item?.label}
      </div>

      {/* Created at */}
      <div className="flex-1">{formatWithTime(new Date(email.createdAt * 1000))}</div>

      {/* Hover Actions Menu */}
      <HoverActionsMenu onOpen={() => onOpen(email)} onDelete={() => onDelete(email)} />
    </div>
  );
};

const ListViewEmail = ({ appView, items, onSearchTasks }: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();

  const project = useSelector((state: any) => state.projectReducer.get("project"));

  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const groups = useMemo(() => {
    const getPlainText = (html: string) => {
      if (!html) return "";
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    };

    const emails = items.map((item) => {
      const createdAt = item.getIn(["event", "createdAt"]);
      const summary = item.getIn(["event", "data", "summary"]) || "";
      const description = item.getIn(["event", "data", "description"]) || "";
      const provider = item.getIn(["event", "data", "provider"]) || "";
      const messageId = item.getIn(["event", "data", "messageId"]) || "";
      const threadId = item.getIn(["event", "data", "threadId"]) || "";
      const emailStatus = item.getIn(["event", "data", "emailStatus"]) || "";
      const sendAtUtc = item.getIn(["event", "data", "sendAtUtc"]);
      const attendees = item.getIn(["event", "data", "attendees"])?.toJS() || [];
      const cc = item.getIn(["event", "data", "cc"])?.toJS() || [];
      const bcc = item.getIn(["event", "data", "bcc"])?.toJS() || [];
      const members = project.get("members")?.toJS() || [];
      const accountId = item.getIn(["event", "person"]);
      const member = members.find((member) => member.accountId == accountId) || {
        accountId,
        displayName: accountId,
      };
      const itemData = item.getIn(["event", "data", "item"]) || fromJS({});
      const tracking = item.getIn(["event", "data", "tracking"]);
      const visibility = item.getIn(["event", "data", "visibility"]);
      return {
        id: item.get("id"),
        itemId: item.get("itemId"),
        title: summary,
        content: description,
        preview: getPlainText(description),
        recipients: [...new Set<string>(attendees)],
        cc,
        bcc,
        provider,
        messageId,
        threadId,
        emailStatus,
        sendAtUtc,
        senderName: member.displayName,
        senderAvatar: member.avatarUrl,
        createdAt,
        item: itemData.toJS(),
        tracking,
        visibility,
      };
    });
    return groupByTime(emails);
  }, [items, project]);

  const handleOpen = (email: EmailData) => {
    if (appView === "Tracked") {
      if (isGmailContext() && !!email.threadId) {
        try {
          window.inboxSdk.Router.goto(`all/${email.threadId}`);
        } catch {}
      } else {
        dispatch(
          TaskAction.showRightPanelDialog(
            {
              messageId: email.messageId,
              threadId: email.threadId,
            },
            DialogTypes.sideMyInboxMessageDialog
          )
        );
      }
    } else {
      dispatch(
        ActivityAction.showEmailDialog(projectId, {
          id: email.id,
          itemId: email.itemId,
          to: email.recipients,
          cc: email.cc,
          bcc: email.bcc,
          subject: email.title,
          body: email.content,
          provider: email.provider,
          threadId: email.threadId,
          emailStatus: email.emailStatus,
          sendAtUtc: email.sendAtUtc,
          item: email.item,
          tracking: email.tracking,
          visibility: email.visibility,
        })
      );
    }
  };

  const handleDeleteClick = (email: EmailData) => {
    setShowConfirm({
      show: true,
      title: t("Delete draft email"),
      description: t("Are you sure you want to remove this draft email?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "DELETE",
      data: email,
    });
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "DELETE") {
      const { id } = showConfirm.data;
      if (!id) {
        setShowConfirm({ show: false, title: "", description: "" });
        return;
      }
      deleteDraftEmail(projectId, id)
        .then(() => {
          onSearchTasks({ projectId });
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete draft email"),
              type: "success",
              message: t("Delete draft email successfully!"),
            })
          );
        })
        .catch(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete draft email"),
              type: "error",
              message: t("Failed to delete draft email"),
            })
          );
        });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const handleGroupToggle = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <div className="w-full flex flex-col gap-3">
      {items.size > 0 && (
        <div className="w-full">
          <div className="flex items-center justify-start gap-2 py-2 bg-gray-100">
            <div className="pl-4 flex-2">{t("Email")}</div>
            <div className="flex-2">{t("Sent to")}</div>
            <div className="flex-2">{t("Item")}</div>
            <div className="flex-1">{t("Created at")}</div>
          </div>
        </div>
      )}

      {/* Incomplete emails grouped by time */}
      {groups.today.length > 0 && (
        <GroupBlock
          label="Today"
          count={groups.today.length}
          isCollapsed={collapsedGroups.has("Today")}
          onToggle={() => handleGroupToggle("Today")}
        >
          {groups.today.map((email) => (
            <EmailItem
              key={email.id}
              email={email}
              onOpen={handleOpen}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      )}

      {groups.thisWeek.length > 0 && (
        <GroupBlock
          label="This week"
          count={groups.thisWeek.length}
          isCollapsed={collapsedGroups.has("This week")}
          onToggle={() => handleGroupToggle("This week")}
        >
          {groups.thisWeek.map((email) => (
            <EmailItem
              key={email.id}
              email={email}
              onOpen={handleOpen}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      )}

      {Object.entries(groups.months).map(([label, list]) => (
        <GroupBlock
          key={label}
          label={label}
          count={list.length}
          isCollapsed={collapsedGroups.has(label)}
          onToggle={() => handleGroupToggle(label)}
        >
          {list.map((email) => (
            <EmailItem
              key={email.id}
              email={email}
              onOpen={handleOpen}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      ))}

      {items.size === 0 && appView === "Drafts" && (
        <ListViewEmptyState
          icon={Mail}
          title="Emails"
          description="No emails yet! Create your first email to get started."
          actionLabel="Compose email"
          onAction={() => dispatch(ActivityAction.showEmailDialog(projectId, null))}
        />
      )}

      {items.size === 0 && appView === "Outbox" && (
        <ListViewEmptyState
          icon={ArchiveRestore}
          title="Email outbox"
          description="Emails scheduled to be sent will appear here"
        />
      )}

      {items.size === 0 && appView === "Tracked" && (
        <ListViewEmptyState
          icon={Eye}
          title="Tracked"
          description="Tracked emails will appear here"
        />
      )}

      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </div>
  );
};

export default ListViewEmail;
